// True if the text carries any explicit time-frame hint (year, month, quarter,
// season, weekday, holiday, relative phrase, duration, or date). Used by submit()
// to reject timeframe-less questions, and by handleChatMessage to recognize a
// standalone dated yes/no question as a new-forecast request.
export function hasTimeframeHint(text) {
  const t = (text || "").toLowerCase();
  return (
    // A bare four-digit year — but NOT a currency amount ("$2000", "€2000"): a price that
    // happens to fall in 1900–2099 is not a timeframe (#3517). The lookbehind rejects a
    // digit run immediately preceded by a currency symbol; a space-separated year ("by 2027")
    // still matches.
    /(?<![$€£¥])\b(19|20)\d{2}\b/.test(t) ||
    // Unambiguous month names ONLY. "may" (modal), "march" (verb) and "mar" are homographs —
    // they'd fire on "prices may rise" / "protesters march" — so they are NOT matched here;
    // a genuine "in May" / "by March" is caught by the preposition-qualified pattern below.
    /\b(january|february|april|june|july|august|september|october|november|december|jan|feb|apr|jun|jul|aug|sept?|oct|nov|dec)\b/.test(
      t,
    ) ||
    /\bq[1-4]\b/.test(t) ||
    // "quarter" only. Seasons (spring/summer/fall/autumn/winter) are verb homographs
    // ("stocks fall", "prices spring back") — they count as a timeframe only when
    // preposition-qualified ("by fall", "in spring"), which the pattern below handles.
    /\bquarter\b/.test(t) ||
    /\b(today|tomorrow|tonight|yesterday|now|currently|imminent|imminently|soon|eventually)\b/.test(
      t,
    ) ||
    /\b\d+\s*(hour|day|week|month|quarter|year|decade|centur)/.test(t) ||
    /\b(by|before|until|within|after|in|over|coming|upcoming|next|this|last|end\s+of|start\s+of|mid|middle\s+of|beginning\s+of)\s+(the\s+)?(\w+\s+){0,2}(hour|day|week|month|quarter|year|decade|season|century|spring|summer|fall|autumn|winter|january|february|march|april|may|june|july|august|september|october|november|december|monday|tuesday|wednesday|thursday|friday|saturday|sunday)/.test(
      t,
    ) ||
    /\b(short[- ]term|long[- ]term|near[- ]term|mid[- ]term|deadline|expires?|expiration)\b/.test(
      t,
    ) ||
    /\b\d{1,2}[-/]\d{1,2}([-/]\d{2,4})?\b/.test(t) ||
    /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/.test(t) ||
    /\b(halloween|thanksgiving|christmas|xmas|hanukkah|kwanzaa|easter|passover|valentine'?s?(\s+day)?|valentines(\s+day)?|st\.?\s*patrick'?s?(\s+day)?|memorial\s+day|labor\s+day|independence\s+day|4th\s+of\s+july|july\s+4(th)?|earth\s+day|tax\s+day|black\s+friday|cyber\s+monday|mother'?s?\s+day|father'?s?\s+day|presidents?\s+day|columbus\s+day|veterans?\s+day|mlk\s+day|martin\s+luther\s+king\s+day|juneteenth|good\s+friday|election\s+day|inauguration\s+day|new\s+year'?s?(\s+(eve|day))?|back[- ]to[- ]school|midterms?|(midterm|general|presidential|primary|congressional|gubernatorial|federal|state|local)\s+elections?|primaries)\b/.test(
      t,
    ) ||
    // Recurring sporting / cultural events without a year — preprocess resolves
    // each to its next upcoming occurrence (same next-occurrence path holidays use).
    /\b(super\s?bowl|world\s+cup|(summer\s+|winter\s+)?olympics?|olympic\s+games|paralympics?|world\s+series|nba\s+finals|stanley\s+cup|final\s+four|march\s+madness|ncaa\s+tournament|wimbledon|kentucky\s+derby|oscars?|academy\s+awards|grammys?|emmys?|golden\s+globes?|met\s+gala|comic[- ]?con)\b/.test(
      t,
    )
  );
}

// True when the message reads as a standalone yes/no forecast question with a
// timeframe — i.e. unambiguously a request for a NEW forecast, not a tweak to the
// active one or a chat aside. Every example question qualifies (they all end with
// "?" and carry a date). handleChatMessage uses this to skip the LLM intent
// classifier for clear new-forecast asks. Messages addressed to the assistant or
// wh-/meta-questions ("what will…", "can you explain…") are deliberately excluded.
export function isLikelyForecastQuestion(text) {
  const t = (text || "").trim();
  if (!t || !hasTimeframeHint(t)) return false;
  if (
    /^(what|why|how|when|where|who|which|explain|tell|show|describe|can you|could you|would you|do you|did you|are you|will you|please)\b/i.test(
      t,
    )
  )
    return false;
  const binaryStart =
    /^(will|won't|is|isn't|are|aren't|am|can|can't|could|should|shouldn't|would|wouldn't|does|doesn't|do|don't|did|didn't|has|hasn't|have|haven't|had|was|wasn't|were|weren't|shall|might|may)\b/i.test(
      t,
    );
  const endsQ = /\?\s*$/.test(t);
  return binaryStart || endsQ;
}

// Looser than isLikelyForecastQuestion: a forecast *attempt* — a yes/no question —
// even if it's missing a timeframe (submit() then prompts for one). Used when
// nothing is active to decide forecast-vs-chat. Excludes assistant-directed and
// wh-/meta-questions ("what does Sayer do", "how do I…", "can you …") so general
// questions get a chat answer instead of a "needs a timeframe" rejection.
export function looksLikeForecastIntent(text) {
  const t = (text || "").trim();
  if (!t) return false;
  if (
    /^(what|whats|what's|why|how|when|where|who|whose|whom|which|explain|tell|show|describe|list|help|can you|could you|would you|do you|did you|are you|will you|is sayer|please)\b/i.test(
      t,
    )
  )
    return false;
  if (isLikelyForecastQuestion(t)) return true;
  // Yes/no auxiliary/modal start (not addressed to the assistant) reads as a
  // forecast even without an explicit timeframe.
  return /^(will|won't|is|isn't|are|aren't|am|can|can't|could|should|shouldn't|would|wouldn't|does|doesn't|do|don't|did|didn't|has|hasn't|have|haven't|had|was|wasn't|were|weren't|shall|might|may)\b/i.test(
    t,
  );
}

// True when the message reads as an OPEN / MULTI-OUTCOME forecast question — a
// wh-question about the world with a time horizon ("how will X compare next year",
// "how many rate cuts in 2026", "which party wins in 2028", "what will CPI be in Dec
// 2026", "who wins the 2026 Super Bowl"). Used ONLY when automatic question-type routing
// is on (the `chatboard-auto-question-type` flag), so these reach submit() →
// /preprocess/auto (which classifies binary/categorical/numeric) instead of being
// deflected to chat by the binary-only looksLikeForecastIntent (which excludes every
// wh-opener and demands a yes/no verb).
//
// It recognises the SHAPE rather than an enumerated opener list (the old list missed
// "how will …", "when will …", etc.): a leading wh-word (how/what/which/who/when/where)
// or a comparison imperative, WITH a timeframe, that is not meta/how-to and not addressed
// to the assistant/user. Yes/no questions ("will …") are deliberately NOT here — those
// are handled by looksLikeForecastIntent. Guardrails against false positives:
//   * a timeframe hint is REQUIRED (hasTimeframeHint) — meta/how-to questions rarely
//     carry a year/date, and forecast questions almost always do;
//   * how-to / about-the-app / explanatory openers ("how do I", "what is a forecast",
//     "why …", "explain …", any mention of "sayer") are excluded;
//   * assistant/user-directed phrasings (a modal/aux + I/you/we, e.g. "should I", "will
//     you") are excluded.
export function looksLikeMultiForecastIntent(text) {
  const t = (text || "").trim();
  if (!t || !hasTimeframeHint(t)) return false;
  // About the assistant/app — never a forecast.
  if (/\bsayer\b/i.test(t)) return false;
  // How-to / meta / about-the-app / explanatory / assistant-directed openers.
  if (
    /^(how (do|does|to|can i|could i|should i|would i|about)|what (is|are|does|do) (a |an |the )?(forecast|sayer|this|that|app|it|i|you)|why\b|explain\b|tell me\b|show me\b|list\b|help\b|can you\b|could you\b|would you\b|do you\b|are you\b|will you\b|did you\b)/i.test(
      t,
    )
  )
    return false;
  // Addressed to the assistant/user: a modal/aux immediately followed by I/you/we
  // ("what will you do tomorrow", "which one should I pick this week").
  if (/\b(should|shall|can|could|would|will|do|does|did|are|have)\s+(i|you|we)\b/i.test(t))
    return false;
  // A wh-/comparison forecast opener about the world (yes/no "will …" is handled by
  // looksLikeForecastIntent). "why" is intentionally excluded above (explanatory, not a
  // forecast request).
  return /^(how|what|which|who|when|where|compare\b|rank\b)\b/i.test(t);
}

// Normalize a short confirmation reply for matching: lowercase, drop ALL
// sentence punctuation (so "no, thanks" / "yes!" match), collapse whitespace.
const normalizeReply = (text) =>
  (text || "")
    .toLowerCase()
    .replace(/[!.?,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Affirmative reply to a yes/no confirmation prompt — "yes", "yep", "new one",
// "a new one!", "go ahead", "do it", "forecast it". handleChatMessage uses this
// to resolve the "run this as a NEW forecast?" prompt DETERMINISTICALLY instead
// of round-tripping the reply back through the LLM classifier (which fumbled
// "new one" — it re-greeted or just sat there). Matches the WHOLE reply (anchored
// `^…$`), not a prefix, so a longer message that merely STARTS with a keyword
// ("please explain", "yes but what about…") is NOT treated as a bare confirmation
// — it falls through to normal routing. Punctuation-tolerant; tolerates a leading
// article ("a new one"). "please" alone is filler, not an affirmative.
export function isAffirmativeReply(text) {
  const t = normalizeReply(text);
  return /^(a |an )?(yes|yes please|yep|yeah|yup|ya|sure|ok|okay|k|y|new|new one|fresh|fresh one|another|another one|do it|go|go ahead|forecast it|run it|proceed|new forecast)$/.test(
    t,
  );
}
// Negative reply to that same confirmation — "no", "nope", "the current one",
// "stay here". Also matches the WHOLE reply (anchored) so a follow-up that starts
// with "no" but keeps going ("no but actually…") is NOT a definitive negative — it
// falls through and is reprocessed as a brand-new message. A reply that is NEITHER
// affirmative nor negative is likewise treated as a brand-new message.
export function isNegativeReply(text) {
  const t = normalizeReply(text);
  return /^(a |an )?(no|nope|nah|not now|no thanks|current|this one|the current|the current one|about this|about the current|stay|stay here|keep going|keep)$/.test(
    t,
  );
}
